import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  NbDialogRef,
  NbToastrService
} from '@nebular/theme';

import { FirebaseRepositoryService } from '../../firebase-repository.service';

interface Producto {
  identifier: number;
  name: string;
  [key: string]: any;
}

interface VariedadCultivo {
  codCultivo: number;
  codEppo: string;
  identifier: number;
  name: string;
}

interface RecintoImportacion {
  idsigpac: string;
  codCultivo: number;
  cultivo: string;
  codVariedad: number;
  variedad: string;
}

@Component({
  selector: 'ngx-importar-recintos',
  templateUrl: './importar-recintos.component.html',
  styleUrls: ['./importar-recintos.component.scss']
})
export class ImportarRecintosComponent implements OnInit {

  userData: any;

  cultivos: Producto[] = [];

  variedades: VariedadCultivo[] = [];

  variedadesFiltradas: VariedadCultivo[] = [];

  idsigpac = '';

  cultivoSeleccionado: number | null = null;

  variedadSeleccionada: number | null = null;

  recintos: RecintoImportacion[] = [];

  loading = false;

  cargandoDatos = true;

  constructor(
    protected dialogRef: NbDialogRef<ImportarRecintosComponent>,
    private repoService: FirebaseRepositoryService,
    private toastrService: NbToastrService,
    private httpClient: HttpClient
  ) {}

  ngOnInit(): void {

    this.cargarCultivos();

    this.cargarVariedades();
  }

  private cargarCultivos(): void {

    this.httpClient.get<Producto[]>('../assets/jsondata/productos.json').subscribe({
        next: items => {
          this.cultivos = items || [];
          this.comprobarCargaDatos();
        },

        error: error => {
          console.error('Error al cargar productos.json:', error);
          this.toastrService.danger(
            'No se han podido cargar los cultivos.',
            'Error'
          );

          this.comprobarCargaDatos();
        }
      });
  }

  private cargarVariedades(): void {

    this.httpClient.get<VariedadCultivo[]>('../assets/jsondata/variedadCultivo.json').subscribe({
        next: items => {
          this.variedades = items || [];
          this.comprobarCargaDatos();
        },

        error: error => {
          console.error('Error al cargar variedadCultivo.json:', error);
          this.toastrService.danger(
            'No se han podido cargar las variedades.',
            'Error'
          );

          this.comprobarCargaDatos();
        }
      });
  }

  private cargasCompletadas = 0;

  private comprobarCargaDatos(): void {

    this.cargasCompletadas++;

    if (this.cargasCompletadas >= 2) {
      this.cargandoDatos = false;
    }
  }

  cambioCultivo(): void {

    this.variedadSeleccionada = null;
    if (this.cultivoSeleccionado == null) {
      this.variedadesFiltradas = [];
      return;
    }

    this.variedadesFiltradas =
      this.variedades.filter(
        variedad =>
          variedad.codCultivo ===
          this.cultivoSeleccionado
      );
  }

  anadirRecinto(): void {

    const idsigpac = (this.idsigpac || '').trim();

    if (!this.validarIdsigpac(idsigpac)) {
      this.toastrService.warning(
        'El valor correspondiente al IDSIGPAC debe tener el formato PROVINCIA, MUNICIPIO, AGREGADO, ZONA, POLÍGONO, PARCELA, RECINTO. Ejemplo: 16,254,0,0,10,138,1',
        'Formato incorrecto'
      );
      return;
    }

    if (this.cultivoSeleccionado == null) {
      this.toastrService.warning(
        'Debe seleccionar un cultivo.',
        'Cultivo'
      );
      return;
    }

    if (this.variedadSeleccionada == null) {

      this.toastrService.warning(
        'Debe seleccionar una variedad.',
        'Variedad'
      );
      return;
    }

    /**
     * Evitamos recintos duplicados.
     */
    const existe = this.recintos.some(recinto => recinto.idsigpac === idsigpac);

    if (existe) {
      this.toastrService.warning(
        'Este recinto ya ha sido añadido.',
        'Recinto duplicado'
      );
      return;
    }

    const cultivo = this.cultivos.find(
      item => item.identifier === this.cultivoSeleccionado
    );

    const variedad = this.variedades.find(
      item =>
        item.codCultivo === this.cultivoSeleccionado &&
        item.identifier === this.variedadSeleccionada
    );

    this.recintos.push({
      idsigpac: idsigpac,
      codCultivo: this.cultivoSeleccionado,
      cultivo: cultivo?.name || '',
      codVariedad: this.variedadSeleccionada,
      variedad: variedad?.name || ''
    });

    /**
     * Limpiamos la línea para poder
     * introducir otro recinto.
     */
    this.idsigpac = '';

    this.cultivoSeleccionado = null;

    this.variedadSeleccionada = null;

    this.variedadesFiltradas = [];
  }

  private validarIdsigpac(
    idsigpac: string
  ): boolean {

    const partes =
      idsigpac.split(',');

    /**
     * Debe tener 7 partes.
     */
    if (partes.length !== 7) {
      return false;
    }

    /**
     * Todas las partes deben ser
     * números enteros.
     */
    return partes.every(parte => /^\d+$/.test(parte.trim()));
  }

  eliminarRecinto(recinto: RecintoImportacion): void {
    this.recintos = this.recintos.filter(item => item !== recinto);
  }

  importar(): void {

    if (this.recintos.length === 0) {

      this.toastrService.warning(
        'Debe añadir al menos un recinto.',
        'Importar Recintos'
      );

      return;
    }

    const idowner = this.userData?.id;

    if (!idowner) {

      this.toastrService.danger(
        'No se ha podido obtener el identificador del titular.',
        'Error'
      );

      return;
    }

    this.loading = true;

    this.repoService.importarRecintos(idowner, this.recintos).subscribe({

        next: response => {
          console.log('[IMPORTAR_RECINTOS] Response:', response);
          this.loading = false;
          this.toastrService.success(
            'La importación de recintos ha finalizado correctamente.',
            'Importar Recintos'
          );
          this.dialogRef.close(true);
        },

        error: error => {
          console.error('[IMPORTAR_RECINTOS] Error:', error);
          this.loading = false;
          this.toastrService.danger(
            'Ha ocurrido un error durante la importación de los recintos.',
            'Error'
          );
        }
      });
  }

  cerrar(): void {

    if (this.loading) {
      return;
    }

    this.dialogRef.close(false);
  }

  get puedeAnadirRecinto(): boolean {
    return (
      this.idsigpac.trim().length > 0 &&
      this.cultivoSeleccionado !== null &&
      this.variedadSeleccionada !== null &&
      !this.loading &&
      !this.cargandoDatos
    );
  }

  get mensajeSpinner(): string {
    return this.cargandoDatos
      ? 'Cargando cultivos y variedades...'
      : 'Añadiendo recintos, por favor espere...';
  }

}
